import { findClosure } from '../annotation/trace';

/*
 * One finger's stroke over a live camera, read as what it means (2026-10-21;
 * docs/FIELD_AI_VISION_SPEC.md section 8): a tap, a swipe in from an edge, a
 * swipe across the screen (2026-10-25: the hubs' page swipe, decision D12 in
 * docs/UX_AUDIT_2026-10-24.md), a closed loop drawn around something, or
 * nothing at all. Pure, so it is tested on recorded strokes;
 * hooks/useViewportGestures.js feeds it.
 *
 * A stroke is its points in the order the finger made them, {x, y, t}: px
 * from the viewport's top-left, ms. `size` is the viewport's {width, height}.
 *
 * EDGES ARE PHYSICAL. Right is the right of the screen in Hebrew too: the
 * gallery slides in from where it was pulled, so the gesture and the motion
 * agree (the one exception to logical directions, section 8).
 *
 * Two fingers are never a stroke: the hook drops a stroke the moment a second
 * finger lands (a pinch is for zoom: pinchScale).
 */

/** Every threshold, in one place. Fractions are of the viewport. */
export const STROKE = Object.freeze({
  tapSlopPx: 10,       // a tap moves no further than this...
  tapMs: 350,          // ...and is over within this
  edgeBand: 0.12,      // the outer part of the width (left, right) or height (bottom) a swipe starts in
  swipeTravel: 0.2,    // how far toward the centre it must go, of that dimension
  straightness: 0.85,  // chord / path length
  angleDeg: 30,        // from the edge's own axis
  closeRadius: 0.06,   // a loop closes within this, of the shorter side
  minArea: 0.02,       // and encloses at least this, of the frame
});

//: the shortest spread between two fingers a pinch is measured from, px (closer is two fingers landing as one)
export const PINCH_MIN_SPREAD = 16;

/**
 * How far two fingers have spread since they landed: the distance between
 * them now over the distance then (2 = twice as far apart). `start` and `now`
 * are each two points {x, y}. 1 where the start was too close to measure.
 */
export function pinchScale(start, now) {
  const from = Math.hypot(start[1].x - start[0].x, start[1].y - start[0].y);
  if (!(from >= PINCH_MIN_SPREAD)) return 1;
  return Math.hypot(now[1].x - now[0].x, now[1].y - now[0].y) / from;
}

// Toward the centre, from each edge: [dx, dy].
const INWARD = Object.freeze({ left: [1, 0], right: [-1, 0], bottom: [0, -1] });

export const EDGES = Object.freeze(Object.keys(INWARD));

/** Whether a point lies in an edge's band. */
export function inEdgeBand(point, size, edge, band = STROKE.edgeBand) {
  if (edge === 'left') return point.x <= size.width * band;
  if (edge === 'right') return point.x >= size.width * (1 - band);
  if (edge === 'bottom') return point.y >= size.height * (1 - band);
  return false;
}

function pathLength(points) {
  let length = 0;
  for (let i = 1; i < points.length; i += 1) {
    length += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
  }
  return length;
}

// Across the screen, from anywhere: [dx, dy] of each direction. 'right' is a
// finger moving left to right, in Hebrew too: directions are physical.
const ACROSS = Object.freeze({ right: [1, 0], left: [-1, 0] });

/** A straight swipe across the screen in `direction`, wherever it began. */
function swipeAcross(direction, points, size, rules) {
  const [dx, dy] = ACROSS[direction];
  const first = points[0];
  const last = points[points.length - 1];
  const cx = last.x - first.x;
  const cy = last.y - first.y;
  const along = cx * dx + cy * dy;
  if (along < rules.swipeTravel * size.width) return false;
  const chord = Math.hypot(cx, cy);
  const path = pathLength(points);
  if (!path || chord / path < rules.straightness) return false;
  return (Math.acos(Math.min(1, along / chord)) * 180) / Math.PI <= rules.angleDeg;
}

function swipeFrom(edge, points, size, rules) {
  const first = points[0];
  const last = points[points.length - 1];
  if (!inEdgeBand(first, size, edge, rules.edgeBand)) return false;
  const [dx, dy] = INWARD[edge];
  const cx = last.x - first.x;
  const cy = last.y - first.y;
  const along = cx * dx + cy * dy;
  const dimension = edge === 'bottom' ? size.height : size.width;
  if (along < rules.swipeTravel * dimension) return false;
  const chord = Math.hypot(cx, cy);
  const path = pathLength(points);
  if (!path || chord / path < rules.straightness) return false;
  const angle = (Math.acos(Math.min(1, along / chord)) * 180) / Math.PI;
  return angle <= rules.angleDeg;
}

/** The first loop the stroke closed, in viewport px, or null. A finished
 *  stroke is read the way it was drawn: the loop counts where it closed,
 *  whatever the finger did after. */
function loopOf(points, size, rules) {
  const shorter = Math.min(size.width, size.height);
  const closeRadius = rules.closeRadius * shorter;
  const options = { closeRadius, ignoreLength: 2 * closeRadius, minArea: rules.minArea * size.width * size.height };
  const xy = points.map(p => [p.x, p.y]);
  for (let n = 4; n <= xy.length; n += 1) {
    const hit = findClosure(xy.slice(0, n), options);
    if (hit) return hit.loop.map(([x, y]) => ({ x, y }));
  }
  return null;
}

/**
 * What the stroke was: {kind: 'tap', x, y}, {kind: 'edgeSwipe', edge},
 * {kind: 'swipe', direction}, {kind: 'lasso', loop} or {kind: 'none'}.
 *
 * `edges` are the ones this viewport has a sheet for; any other edge's swipe
 * is nothing. `swipes` are the directions ('right': left to right, 'left')
 * read as a swipe across the screen; an edge swipe wins over one. `lasso`
 * says whether a loop is read at all (the encircle, phase A3); a loop must
 * start outside every enabled band.
 */
export function recognise(points, size, { edges = [], swipes = [], lasso = false, rules = STROKE } = {}) {
  if (!points?.length || !(size?.width > 0) || !(size?.height > 0)) return { kind: 'none' };
  const first = points[0];
  const last = points[points.length - 1];
  const moved = Math.max(...points.map(p => Math.hypot(p.x - first.x, p.y - first.y)));
  if (moved <= rules.tapSlopPx) {
    return last.t - first.t <= rules.tapMs ? { kind: 'tap', x: first.x, y: first.y } : { kind: 'none' };
  }
  for (const edge of edges) {
    if (INWARD[edge] && swipeFrom(edge, points, size, rules)) return { kind: 'edgeSwipe', edge };
  }
  for (const direction of swipes) {
    if (ACROSS[direction] && swipeAcross(direction, points, size, rules)) return { kind: 'swipe', direction };
  }
  if (lasso && !edges.some(edge => inEdgeBand(first, size, edge, rules.edgeBand))) {
    const loop = loopOf(points, size, rules);
    if (loop) return { kind: 'lasso', loop };
  }
  return { kind: 'none' };
}
